import React, {
  createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import type { HubTagTemplate } from '../../dorian-shared/types/branch/HubTagTemplate';
import { useApiService } from '../ApiServiceContext/ApiServiceContext';
import { showToast } from '../../components/ui/utils';

type FetchTemplatesOptions = {
  includeUsage?: boolean;
};

type HubTagTemplatesContextType = {
  isLoading: boolean;
  templates: HubTagTemplate[];
  bookId: number;
  fetchTemplates: (abortController?: AbortController, options?: FetchTemplatesOptions) => Promise<HubTagTemplate[]>;
  createTemplate: (template: Omit<HubTagTemplate, 'id' | 'uuid' | 'bookId' | 'usedInStory'>) => Promise<HubTagTemplate>;
  updateTemplate: (id: number, template: Omit<HubTagTemplate, 'id' | 'uuid' | 'bookId' | 'usedInStory'>) => Promise<HubTagTemplate>;
  deleteTemplate: (id: number) => Promise<void>;
};

const initialContext: HubTagTemplatesContextType = {
  isLoading: false,
  templates: [],
  bookId: 0,
  fetchTemplates: async () => [],
  createTemplate: async () => ({ name: '', text: null, color: null, iconId: null }),
  updateTemplate: async () => ({ name: '', text: null, color: null, iconId: null }),
  deleteTemplate: async () => {},
};

export const HubTagTemplatesContext = createContext<HubTagTemplatesContextType>(initialContext);

export const useHubTagTemplatesContext = () => useContext(HubTagTemplatesContext);

type HubTagTemplatesContextProviderProps = {
  children: ReactNode;
  bookId: number;
};

export function HubTagTemplatesContextProvider(props: HubTagTemplatesContextProviderProps) {
  const { children, bookId } = props;
  const [isLoading, setIsLoading] = useState(false);
  const [templates, setTemplates] = useState<HubTagTemplate[]>([]);

  const apiService = useApiService();

  const fetchTemplates = useCallback(async (
    abortController?: AbortController,
    options?: FetchTemplatesOptions,
  ) => {
    setIsLoading(true);
    try {
      const response = await apiService.fetchHubTagTemplatesByBookId(
        bookId,
        abortController,
        { includeUsage: options?.includeUsage === true },
      );
      setTemplates(response);
      return response;
    } finally {
      setIsLoading(false);
    }
  }, [apiService, bookId]);

  const createTemplate = useCallback(async (template: Omit<HubTagTemplate, 'id' | 'uuid' | 'bookId' | 'usedInStory'>) => {
    const response = await apiService.createHubTagTemplate(bookId, template);
    setTemplates((prev) => [...prev, response]);
    return response;
  }, [apiService, bookId]);

  const updateTemplate = useCallback(async (id: number, template: Omit<HubTagTemplate, 'id' | 'uuid' | 'bookId' | 'usedInStory'>) => {
    const response = await apiService.updateHubTagTemplate(bookId, id, template);
    // PUT response omits usedInStory, so merge to avoid dropping it.
    setTemplates((prev) => prev.map((t) => (t.id === id ? { ...t, ...response } : t)));
    return response;
  }, [apiService, bookId]);

  const deleteTemplate = useCallback(async (id: number) => {
    await apiService.deleteHubTagTemplate(bookId, id);
    setTemplates((prev) => prev.filter((t) => t.id !== id));
  }, [apiService, bookId]);

  useEffect(() => {
    if (!bookId) {
      return () => {};
    }
    const abortController = new AbortController();
    fetchTemplates(abortController).catch(() => {
      showToast({ textMessage: 'Failed to fetch tag templates' });
    });
    return () => {
      abortController.abort();
    };
  }, [bookId, fetchTemplates]);

  const value = useMemo(() => ({
    isLoading,
    templates,
    bookId,
    fetchTemplates,
    createTemplate,
    updateTemplate,
    deleteTemplate,
  }), [
    isLoading,
    templates,
    bookId,
    fetchTemplates,
    createTemplate,
    updateTemplate,
    deleteTemplate,
  ]);

  return (
    <HubTagTemplatesContext.Provider value={value}>
      {children}
    </HubTagTemplatesContext.Provider>
  );
}
